import React from 'react';
import { CopyPlus, Pencil, Trash2 } from 'lucide-react';

import CodeChip from './CodeChip';
import { CHECKBOX_CLASS } from './CouponTable';
import { ExpiringChip, StatusChip, TypeChip, UsageMeter } from './CouponMeta';
import { formatDateRange } from './couponAdapter';
import { formatCouponAmount, getCouponStatus } from './couponStatus';
import { MiniLoader } from '../../reuse/SVG';

/**
 * The same rows as CouponTable, stacked, for viewports below `md`.
 *
 * A separate component rather than a responsive table: the desktop layout needs six columns to
 * stay aligned, and squeezing those into a phone width either scrolls horizontally (what the old
 * page did) or collapses into unreadable slivers.
 */
const CouponCardList = ({
    coupons,
    highlightId,
    togglingId,
    selectedIds,
    onSelect,
    onOpen,
    onClone,
    onToggle,
    onDelete,
    onCopy,
}) => (
    <ul className="cpn-divide-y cpn-divide-neutral-100 md:cpn-hidden">
        {coupons.map(coupon => {
            const selected = selectedIds.includes(coupon.id);
            return (
            <li
                key={coupon.id}
                className={`cpn-px-4 cpn-py-4 cpn-transition ${
                    highlightId === coupon.id
                        ? 'cpn-bg-brand-50 cpn-ring-2 cpn-ring-inset cpn-ring-brand-400'
                        : selected
                        ? 'cpn-bg-brand-50/60'
                        : ''
                }`}
            >
                <div className="cpn-flex cpn-items-start cpn-gap-3">
                    <input
                        type="checkbox"
                        checked={selected}
                        onChange={event => onSelect(coupon.id, event.target.checked)}
                        aria-label={`Select ${coupon.name}`}
                        className={`${CHECKBOX_CLASS} cpn-mt-1 cpn-shrink-0`}
                    />
                    <button
                        type="button"
                        onClick={() => onOpen(coupon)}
                        className="cpn-block cpn-w-full cpn-text-left"
                    >
                        <span className="cpn-block cpn-font-semibold cpn-text-neutral-900">{coupon.name}</span>
                    </button>
                </div>

                <div className="cpn-mt-2 cpn-flex cpn-flex-wrap cpn-items-center cpn-gap-2">
                    <CodeChip code={coupon.code} onCopied={onCopy} />
                    <StatusChip status={getCouponStatus(coupon)} />
                    <TypeChip type={coupon.type} />
                    <ExpiringChip coupon={coupon} />
                </div>

                <p className="cpn-mt-2 cpn-text-xs cpn-text-neutral-500">
                    {formatDateRange(coupon.startDate, coupon.endDate)}
                </p>

                <div className="cpn-mt-3 cpn-flex cpn-items-end cpn-justify-between cpn-gap-4">
                    <UsageMeter coupon={coupon} />
                    <p className="cpn-text-lg cpn-font-bold cpn-tabular-nums cpn-text-neutral-900">
                        {formatCouponAmount(coupon)}
                    </p>
                </div>

                <div className="cpn-mt-3 cpn-flex cpn-items-center cpn-gap-1.5 cpn-border-t cpn-border-neutral-100 cpn-pt-3">
                    {togglingId === coupon.id ? (
                        <span className="coupon_toggle_loader cpn-mr-2 cpn-flex cpn-h-6 cpn-w-11 cpn-items-center cpn-justify-center">
                            <MiniLoader />
                        </span>
                    ) : (
                        <button
                            type="button"
                            role="switch"
                            aria-checked={coupon.active}
                            aria-label={`${coupon.active ? 'Deactivate' : 'Activate'} ${coupon.name}`}
                            onClick={() => onToggle(coupon)}
                            className={`cpn-relative cpn-mr-2 cpn-h-6 cpn-w-11 cpn-shrink-0 cpn-rounded-full cpn-transition ${
                                coupon.active ? 'cpn-bg-emerald-500' : 'cpn-bg-neutral-300'
                            }`}
                        >
                            <span
                                className={`cpn-absolute cpn-top-0.5 cpn-h-5 cpn-w-5 cpn-rounded-full cpn-bg-white cpn-shadow-sm cpn-transition-all ${
                                    coupon.active ? 'cpn-left-[22px]' : 'cpn-left-0.5'
                                }`}
                            />
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => onOpen(coupon)}
                        className="cpn-inline-flex cpn-items-center cpn-gap-1.5 cpn-rounded-lg cpn-px-2.5 cpn-py-2 cpn-text-xs cpn-font-semibold cpn-text-neutral-600 cpn-transition hover:cpn-bg-brand-50 hover:cpn-text-brand-700"
                    >
                        <Pencil className="cpn-h-3.5 cpn-w-3.5" aria-hidden="true" />
                        Edit
                    </button>
                    <button
                        type="button"
                        onClick={() => onClone(coupon)}
                        className="cpn-inline-flex cpn-items-center cpn-gap-1.5 cpn-rounded-lg cpn-px-2.5 cpn-py-2 cpn-text-xs cpn-font-semibold cpn-text-neutral-600 cpn-transition hover:cpn-bg-brand-50 hover:cpn-text-brand-700"
                    >
                        <CopyPlus className="cpn-h-3.5 cpn-w-3.5" aria-hidden="true" />
                        Clone
                    </button>
                    <button
                        type="button"
                        onClick={() => onDelete(coupon)}
                        aria-label={`Delete ${coupon.name}`}
                        className="cpn-ml-auto cpn-rounded-lg cpn-p-2 cpn-text-neutral-400 cpn-transition hover:cpn-bg-red-50 hover:cpn-text-red-600"
                    >
                        <Trash2 className="cpn-h-4 cpn-w-4" aria-hidden="true" />
                    </button>
                </div>
            </li>
            );
        })}
    </ul>
);

export default CouponCardList;
